import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FlaskConical, Wallet, ShoppingBag, Users, Rocket, LogOut, Loader2, X, Store } from "lucide-react";
import { toast } from "sonner";
import api, { isSandbox, setSandboxMode, setAuthToken, gbpOnly as gbp, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const useSandboxActions = () => {
  const { setSession, setUser } = useAuth();
  const navigate = useNavigate();
  const start = async (name = "") => {
    setAuthToken(null);
    setSandboxMode(true);
    try {
      const { data } = await api.post("/sandbox/start", { name });
      setSession(data);
      localStorage.setItem("mk_sandbox_gate", "1");
      toast.success(`Sandbox ready — ${data.store_name} is live with £1,000 of fake money`);
      navigate("/vendor?sandbox=welcome");
    } catch (e) {
      setSandboxMode(false);
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const exit = (goTo = "/") => {
    setAuthToken(null);
    setSandboxMode(false);
    if (setUser) setUser(false);
    window.location.href = goTo;
  };
  const goLive = async () => {
    try {
      const { data } = await api.post("/sandbox/handoff");
      toast.success(`Bringing ${data.store_name} and ${data.products} product${data.products === 1 ? "" : "s"} with you`);
      exit(`/register?role=vendor&handoff=${data.token}`);
    } catch (e) {
      exit("/register?role=vendor");
    }
  };
  return { start, exit, goLive };
};

export const SandboxGate = () => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user, loading } = useAuth();
  const { start } = useSandboxActions();
  useEffect(() => {
    if (loading || user || isSandbox() || localStorage.getItem("mk_sandbox_gate")) return;
    if (["/coming-soon", "/status", "/login", "/register"].some((p) => window.location.pathname.startsWith(p))) return;
    const t = setTimeout(() => setShow(true), localStorage.getItem("mk_visited") === "1" && !sessionStorage.getItem("mk_fresh") ? 3600 : 800);
    return () => clearTimeout(t);
  }, [loading, user]);
  const dismiss = () => { localStorage.setItem("mk_sandbox_gate", "1"); setShow(false); };
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[190] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="sandbox-gate" onClick={dismiss}>
      <div className="bg-white w-full max-w-2xl p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={dismiss} className="absolute top-3 right-3 text-neutral-400 hover:text-black" data-testid="sandbox-gate-close"><X className="w-5 h-5" /></button>
        <span className="label-caps text-[#0047AB]">Welcome to MRQK UK</span>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold mt-1">How would you like to start?</h2>
        <p className="text-sm text-neutral-500 mt-2">Try everything with fake money first, or go straight to setting up your real business. You can switch any time.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <button onClick={async () => { setBusy(true); localStorage.setItem("mk_sandbox_gate", "1"); await start(); setBusy(false); setShow(false); }} disabled={busy} className="text-left border-2 border-amber-400 bg-amber-50 p-4 hover:bg-amber-100 transition-colors disabled:opacity-60" data-testid="sandbox-gate-try">
            <FlaskConical className="w-6 h-6 text-amber-600 mb-2" />
            <p className="font-medium">Try it with fake money</p>
            <p className="text-xs text-neutral-600 mt-1">Instant demo store, £1,000 sandbox wallet, fake customers placing orders. Nothing is real.</p>
            {busy && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
          </button>
          <Link to="/register?role=vendor" onClick={dismiss} className="text-left border border-neutral-300 p-4 hover:border-[#0047AB] transition-colors" data-testid="sandbox-gate-real">
            <Rocket className="w-6 h-6 text-[#0047AB] mb-2" />
            <p className="font-medium">Set up my business</p>
            <p className="text-xs text-neutral-600 mt-1">Create your real store now — free to start, pick a plan when you're ready.</p>
          </Link>
          <button onClick={dismiss} className="text-left border border-neutral-300 p-4 hover:border-neutral-500 transition-colors" data-testid="sandbox-gate-browse">
            <ShoppingBag className="w-6 h-6 text-neutral-700 mb-2" />
            <p className="font-medium">Just browsing</p>
            <p className="text-xs text-neutral-600 mt-1">Shop the marketplace. You'll find "Try with fake money" on the Sell page later.</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export const TrySandboxButton = ({ className = "", label = "Try it free with fake money", testid = "try-sandbox-btn" }) => {
  const [busy, setBusy] = useState(false);
  const { start } = useSandboxActions();
  if (isSandbox()) return null;
  return (
    <button onClick={async () => { setBusy(true); await start(); setBusy(false); }} disabled={busy} className={className} data-testid={testid}>
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <FlaskConical className="w-5 h-5" />}{label}
    </button>
  );
};

export const SandboxBanner = () => {
  const { user } = useAuth();
  const { exit, goLive } = useSandboxActions();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const active = isSandbox();
  const load = () => api.get("/sandbox/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => {
    if (!active || !user) return;
    load();
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.post("/sandbox/tick");
        if (data.created) { toast.success(`New sandbox order from ${data.customer} — ${gbp(data.total)} (${data.order_number})`); load(); }
      } catch (e) { /* ignore */ }
    }, 180000);
    return () => clearInterval(timer.current);
  }, [active, user]); // eslint-disable-line
  if (!active) return null;
  const simulate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/sandbox/simulate", { count: 3 }); toast.success(`${data.created} fake customers just ordered from your store`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="bg-amber-400 text-black text-sm" data-testid="sandbox-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-semibold flex items-center gap-1.5"><FlaskConical className="w-4 h-4" />SANDBOX — fake money, nothing here is real</span>
        {me && <span className="flex items-center gap-1" data-testid="sandbox-wallet"><Wallet className="w-4 h-4" />{gbp(me.wallet)} wallet · {me.orders} orders</span>}
        <span className="flex-1" />
        {me?.store_slug && <Link to={`/store/${me.store_slug}`} className="underline flex items-center gap-1" data-testid="sandbox-my-store"><Store className="w-4 h-4" />My demo store</Link>}
        <Link to="/products" className="underline flex items-center gap-1" data-testid="sandbox-shop"><ShoppingBag className="w-4 h-4" />Shop with fake money</Link>
        {user && <button onClick={simulate} disabled={busy} className="underline flex items-center gap-1 disabled:opacity-60" data-testid="sandbox-simulate">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}Simulate customers</button>}
        <button onClick={() => (user ? goLive() : exit("/register?role=vendor"))} className="bg-black text-white px-3 py-1 flex items-center gap-1 hover:bg-neutral-800" data-testid="sandbox-go-live" title="Your store name, branding and products come with you"><Rocket className="w-4 h-4" />Set up for real</button>
        <button onClick={() => exit("/")} className="flex items-center gap-1 hover:underline" data-testid="sandbox-exit"><LogOut className="w-4 h-4" />Exit</button>
      </div>
    </div>
  );
};
